/**
 * マスター・用語・画面（F5）のルート定義。
 *
 * 一覧も詳細と同じ `?pj=` を読む。PJ を選んだ利用者が一覧へ移った瞬間に PJ が
 * 外れると、マスターを読んでいることが画面に出ないまま「PJ を適用した後の姿」を読んだ
 * つもりになる。鍵の名前は画面をまたいで同じ `pj` にしてある（`pj-search.ts`）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面・用語・アクター・ドメイン・動詞・単位の一覧と、画面・用語・分類マスターの詳細のルートを定義する。
 * @graph-connects none
 */
import { createRoute } from '@tanstack/react-router';
import { ActorsPage } from '../ActorsPage.js';
import { DomainsPage } from '../DomainsPage.js';
import { MasterDetailPage } from '../MasterDetailPage.js';
import { parsePjSearch } from '../pj-search.js';
import {
  ACTORS_ROUTE,
  DOMAINS_ROUTE,
  MASTER_DETAIL_ROUTE,
  SCREENS_ROUTE,
  SCREEN_DETAIL_ROUTE,
  TERMS_ROUTE,
  TERM_DETAIL_ROUTE,
  UNITS_ROUTE,
  VERBS_ROUTE,
} from '../route-paths.js';
import { ScreenDetailPage } from '../ScreenDetailPage.js';
import { ScreensPage } from '../ScreensPage.js';
import { TermDetailPage } from '../TermDetailPage.js';
import { TermsPage } from '../TermsPage.js';
import { UnitsPage } from '../UnitsPage.js';
import { VerbsPage } from '../VerbsPage.js';
import { rootRoute } from './root.js';

/** @graph-connects none */
export const screensRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: SCREENS_ROUTE,
  component: ScreensPage,
  validateSearch: parsePjSearch,
});
/** @graph-connects none */
export const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: TERMS_ROUTE,
  component: TermsPage,
  validateSearch: parsePjSearch,
});
/** @graph-connects none */
export const actorsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ACTORS_ROUTE,
  component: ActorsPage,
  validateSearch: parsePjSearch,
});
/** @graph-connects none */
export const domainsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: DOMAINS_ROUTE,
  component: DomainsPage,
  validateSearch: parsePjSearch,
});
/** @graph-connects none */
export const verbsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: VERBS_ROUTE,
  component: VerbsPage,
  validateSearch: parsePjSearch,
});
/** @graph-connects none */
export const unitsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: UNITS_ROUTE,
  component: UnitsPage,
  validateSearch: parsePjSearch,
});
/** @graph-connects none */
export const termDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: TERM_DETAIL_ROUTE,
  component: TermDetailPage,
  validateSearch: parsePjSearch,
});
/** @graph-connects none */
export const screenDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: SCREEN_DETAIL_ROUTE,
  component: ScreenDetailPage,
  validateSearch: parsePjSearch,
});
/** @graph-connects none */
export const masterDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: MASTER_DETAIL_ROUTE,
  component: MasterDetailPage,
  validateSearch: parsePjSearch,
});

/** マスター・用語・画面のルート。ルート木（`app.tsx`）はこれを並べる。 @graph-connects none */
export const mastersRoutes = [
  screensRoute,
  termsRoute,
  actorsRoute,
  domainsRoute,
  verbsRoute,
  unitsRoute,
  termDetailRoute,
  screenDetailRoute,
  masterDetailRoute,
] as const;
